/**
 * What the login and signup pages remember in the browser (#668, #675).
 *
 *  - the DOOR the person last signed in with (`localStorage`): a browser that
 *    used the password opens with the password fold open, every other
 *    browser opens mail-first;
 *  - the PENDING mailed code (`sessionStorage`, per tab): the handle of the
 *    request, so a refresh brings the code field back. Since #675 the record
 *    also says which page asked (`origin`), where the person was going
 *    (`returnTo`) and, for a signup, the two names — what »Pošlji znova«
 *    needs to repeat the request.
 *
 * Neither holds a secret — the handle alone redeems nothing, the code lives
 * only in the mail. The names sit next to the address that was already
 * there: same tab, 15 minutes, gone when the tab closes. Every storage call
 * is guarded: a browser that refuses storage gets the defaults (mail-first,
 * no restored code field), never an exception.
 */

import { safeReturnTo } from '../../shared/utils/safeReturnTo.js';

export type LoginDoor = 'pwd' | 'mail' | 'google' | 'microsoft';

const DOOR_KEY = 'gsx8.lastLoginDoor';
const DOOR_PREV_KEY = 'gsx8.lastLoginDoorPrev';
const PENDING_KEY = 'gsx8.loginLinkPending';

/** The mailed code lives 15 minutes; an older record is a dead handle. */
export const PENDING_LINK_TTL_MS = 15 * 60 * 1000;

const DOORS: readonly LoginDoor[] = ['pwd', 'mail', 'google', 'microsoft'];
const REQUEST_ID_RE = /^[0-9a-f]{32}$/;

function isDoor(value: unknown): value is LoginDoor {
  return typeof value === 'string' && (DOORS as readonly string[]).includes(value);
}

/** The page's handle for a mailed code: exactly what the server mints. */
export function isRequestId(value: unknown): value is string {
  return typeof value === 'string' && REQUEST_ID_RE.test(value);
}

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    /* storage refused — the page works without it */
  }
}

const local = () => window.localStorage;
const session = () => window.sessionStorage;

export function readLastLoginDoor(): LoginDoor | null {
  const value = read(local, DOOR_KEY);
  return isDoor(value) ? value : null;
}

/** Called only after a sign-in the page SAW succeed. */
export function rememberLoginDoor(door: LoginDoor): void {
  write(local, DOOR_KEY, door);
  write(local, DOOR_PREV_KEY, null);
}

/**
 * Google / Microsoft: the success redirect goes straight to returnTo and
 * never passes the login page, so the click is the only moment the page can
 * see. The previous door is kept so a refused return can restore it.
 */
export function beginOAuthDoor(door: 'google' | 'microsoft'): void {
  const previous = readLastLoginDoor();
  write(local, DOOR_KEY, door);
  write(local, DOOR_PREV_KEY, previous ?? '');
}

/**
 * The door the page opens with. A return from a REFUSED OAuth attempt
 * (`?error=oauth_…`) first puts back the door that stood before the click.
 */
export function resolveLoginDoor(search: string): LoginDoor | null {
  const error = new URLSearchParams(search).get('error') ?? '';
  if (error.startsWith('oauth_')) {
    const previous = read(local, DOOR_PREV_KEY);
    if (previous !== null) {
      write(local, DOOR_KEY, isDoor(previous) ? previous : null);
      write(local, DOOR_PREV_KEY, null);
    }
  }
  return readLastLoginDoor();
}

/** Which page asked for the code (#675). */
export type PendingOrigin = 'login' | 'register';

export interface PendingLink {
  requestId: string;
  /** Epoch ms of the send, by this browser's clock. */
  sentAt: number;
  sentTo: string;
  /** The request was a resend — the page says »Velja samo zadnja«. */
  resent: boolean;
  /**
   * #675: `register` = the signup asked. The signup page restores ONLY such a
   * record — the login route hands a handle to an unknown address too, and
   * that handle must never cover the signup form. A record written before
   * #675 has no origin and reads as `login`.
   */
  origin: PendingOrigin;
  /**
   * #675: the destination sent with the request ('' = none). A resend from a
   * page that has no `returnTo` of its own sends this one, or the newest code
   * — the only valid one — would have no way back.
   */
  returnTo: string;
  /** #675, `register` only: the names the signup was asked with. */
  firstName?: string;
  lastName?: string;
}

export function savePendingLink(pending: PendingLink): void {
  write(session, PENDING_KEY, JSON.stringify(pending));
}

export function clearPendingLink(): void {
  write(session, PENDING_KEY, null);
}

/** The stored record, or null when there is none, it is malformed, or it is older than the code's life. */
export function readPendingLink(now: number = Date.now()): PendingLink | null {
  const raw = read(session, PENDING_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPendingLink();
    return null;
  }
  const p = parsed as Partial<PendingLink> | null;
  const valid =
    !!p &&
    typeof p === 'object' &&
    isRequestId(p.requestId) &&
    typeof p.sentAt === 'number' &&
    Number.isFinite(p.sentAt) &&
    typeof p.sentTo === 'string' &&
    p.sentTo !== '';
  // A send time in the future is a broken record, not a fresh one.
  if (!valid || !p || now - (p.sentAt as number) >= PENDING_LINK_TTL_MS || (p.sentAt as number) > now) {
    clearPendingLink();
    return null;
  }

  // #675. No origin = a record from before it = login. Any OTHER value, or a
  // signup record without its names, is a broken record.
  const origin: unknown = p.origin ?? 'login';
  if (origin !== 'login' && origin !== 'register') {
    clearPendingLink();
    return null;
  }
  const firstName = typeof p.firstName === 'string' ? p.firstName.trim() : '';
  const lastName = typeof p.lastName === 'string' ? p.lastName.trim() : '';
  if (origin === 'register' && (!firstName || !lastName)) {
    clearPendingLink();
    return null;
  }

  return {
    requestId: p.requestId as string,
    sentAt: p.sentAt as number,
    sentTo: p.sentTo as string,
    resent: p.resent === true,
    origin,
    // Storage is writable by anything on the origin: the destination passes
    // the same gate as one from the URL before it is ever used.
    returnTo: safeReturnTo(typeof p.returnTo === 'string' ? p.returnTo : null, window.location.origin, ''),
    ...(origin === 'register' ? { firstName, lastName } : {}),
  };
}
